import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { store } from "../../app/store";
import {
  setLanguage,
  setAvailableLanguages,
  setAvailableVariants,
  setDetectedVariant,
  setCurrentVariant,
  setLocationInfo,
  setIsRTL,
  setCurrentRegionalCode,
} from "./localizationSlice";
import { SUPPORTED_LANGUAGES, SUPPORTED_VARIANTS } from "./languages";
import enTranslations from "./locales/en.json";
import deTranslations from "./locales/de.json";
import enUSTranslations from "./locales/en-US.json";
import enGBTranslations from "./locales/en-GB.json";
import deDETranslations from "./locales/de-DE.json";
import deATTranslations from "./locales/de-AT.json";
import ukTranslations from "./locales/uk.json";
import plTranslations from "./locales/pl.json";
import jaTranslations from "./locales/ja.json";
import jaJPTranslations from "./locales/ja-JP.json";
import arTranslations from "./locales/ar.json";
import {
  detectRegionalVariant,
  detectLocationInfo,
  REGIONAL_VARIANTS,
  type RegionalDetectionResult,
} from "./utils/regionalDetection";
import { isRTLLanguage } from "./utils/utils";
import { bindVocabularyToI18n } from "../organization/utils/vocabularyI18n";
import { applyQueryLanguage, readLanguageFromQuery } from "./utils/queryLanguage";
import { bindDocumentLanguage } from "./utils/documentLanguage";

const resources = {
  en: { translation: enTranslations },
  de: { translation: deTranslations },
  uk: { translation: ukTranslations },
  pl: { translation: plTranslations },
  ja: { translation: jaTranslations },
  ar: { translation: arTranslations },
  "en-US": { translation: enUSTranslations },
  "en-GB": { translation: enGBTranslations },
  "de-DE": { translation: deDETranslations },
  "de-AT": { translation: deATTranslations },
  "ja-JP": { translation: jaJPTranslations },
};

const supportedLanguages = [...SUPPORTED_LANGUAGES];
export const supportedVariants = [...SUPPORTED_VARIANTS];

// A marketing-site link carries the language the visitor chose there (?lang=…). It wins over
// the stored and the detected language and is cached like a manual choice, so the next visit
// without the parameter keeps it (ID-0021).
const queryLanguage = applyQueryLanguage(
  readLanguageFromQuery(window.location.search, SUPPORTED_LANGUAGES, SUPPORTED_VARIANTS),
  localStorage.getItem("i18nextLng")
);
if (queryLanguage) {
  try {
    localStorage.setItem("i18nextLng", queryLanguage);
  } catch (error) {
    console.warn("Failed to cache the language from the URL:", error);
  }
}

const storedLanguage = queryLanguage ?? localStorage.getItem("i18nextLng");
const currentBrowserLanguage =
  navigator.language || navigator.languages?.[0] || "en";
const storedBrowserLanguage = localStorage.getItem("storedBrowserLanguage");

let initialLanguage: string;
let initialRegionalCode: string;
let regionalDetectionResult: RegionalDetectionResult;

// Check if the stored language is a regional variant
const isStoredRegionalVariant = storedLanguage && storedLanguage.includes("-");

if (storedLanguage && (
    (isStoredRegionalVariant && supportedVariants.includes(storedLanguage)) || 
    (!isStoredRegionalVariant && supportedLanguages.includes(storedLanguage))
  )) {
  
  if (isStoredRegionalVariant) {
    // We have a stored regional variant
    initialRegionalCode = storedLanguage;
    const variant = REGIONAL_VARIANTS[storedLanguage];
    initialLanguage = variant ? variant.baseLanguage : storedLanguage.split("-")[0];
    
    regionalDetectionResult = {
      variant: variant || REGIONAL_VARIANTS["en-US"],
      source: "fallback",
      confidence: 1,
    };
  } else {
    // We have a stored base language
    initialLanguage = storedLanguage;
    const variantForLanguage =
      Object.values(REGIONAL_VARIANTS).find(
        (v) => v.baseLanguage === storedLanguage
      ) || REGIONAL_VARIANTS["en-US"];
    
    initialRegionalCode = variantForLanguage.code;
    regionalDetectionResult = {
      variant: variantForLanguage,
      source: "fallback",
      confidence: 1,
    };
  }
} else {
  if (storedBrowserLanguage !== currentBrowserLanguage) {
    localStorage.setItem("storedBrowserLanguage", currentBrowserLanguage);
  }

  regionalDetectionResult = detectRegionalVariant(
    supportedVariants,
    supportedLanguages
  );

  initialLanguage = regionalDetectionResult.variant.baseLanguage;
  initialRegionalCode = regionalDetectionResult.variant.code;
}

const locationInfo = detectLocationInfo();
store.dispatch(setLocationInfo(locationInfo));
store.dispatch(
  setDetectedVariant({
    variant: regionalDetectionResult.variant,
    source: regionalDetectionResult.source,
    confidence: regionalDetectionResult.confidence,
  })
);
store.dispatch(setCurrentVariant(regionalDetectionResult.variant));
store.dispatch(setCurrentRegionalCode(initialRegionalCode));
store.dispatch(setLanguage(initialLanguage));
store.dispatch(setIsRTL(isRTLLanguage(initialLanguage)));

store.dispatch(setAvailableLanguages([...supportedLanguages]));
store.dispatch(setAvailableVariants([...supportedVariants]));

const customLanguageDetector = {
  type: "languageDetector" as const,
  async: false,
  detect: () => {
    const currentStoredLanguage = localStorage.getItem("i18nextLng");

    if (currentStoredLanguage) {
      const isRegionalVariant = currentStoredLanguage.includes("-");
      
      if ((isRegionalVariant && supportedVariants.includes(currentStoredLanguage)) ||
          (!isRegionalVariant && supportedLanguages.includes(currentStoredLanguage))) {
        return currentStoredLanguage;
      }
    }

    return initialRegionalCode;
  },
  init: () => {},
  cacheUserLanguage: (lng: string) => {
    try {
      const isRegionalVariant = lng.includes("-");
      
      if ((isRegionalVariant && supportedVariants.includes(lng)) ||
          (!isRegionalVariant && supportedLanguages.includes(lng))) {
        localStorage.setItem("i18nextLng", lng);
      }
    } catch (error) {
      console.warn("Failed to cache language preference:", error);
    }
  },
};

i18n
  .use(customLanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["customLanguageDetector"],
      caches: ["localStorage"],
    },
    react: {
      useSuspense: false,
    },
  });

// The organization's words ({{member}}, {{Sessions}} …) as default
// interpolation variables; the Default preset until the organization loads.
bindVocabularyToI18n(i18n);
bindDocumentLanguage(i18n);

i18n.on("languageChanged", (lng) => {
  const isRegionalVariant = lng.includes("-");
  
  if ((isRegionalVariant && supportedVariants.includes(lng)) ||
      (!isRegionalVariant && supportedLanguages.includes(lng))) {
    
    const currentReduxLanguage = store.getState().i18n.currentLanguage;
    const currentReduxRegionalCode = store.getState().i18n.currentRegionalCode;
    
    if (isRegionalVariant) {
      // Regional variant changed
      if (currentReduxRegionalCode !== lng) {
        const variant = REGIONAL_VARIANTS[lng];
        if (variant) {
          const baseLanguage = variant.baseLanguage;
          
          store.dispatch(setCurrentVariant(variant));
          store.dispatch(setCurrentRegionalCode(lng));
          
          if (currentReduxLanguage !== baseLanguage) {
            store.dispatch(setLanguage(baseLanguage));
            store.dispatch(setIsRTL(isRTLLanguage(baseLanguage)));
          }
        }
      }
    } else {
      // Base language changed
      if (currentReduxLanguage !== lng) {
        store.dispatch(setLanguage(lng));
        store.dispatch(setIsRTL(isRTLLanguage(lng)));

        // Find appropriate variant for this language
        const variantForLanguage = Object.values(REGIONAL_VARIANTS).find(
          (v) => v.baseLanguage === lng
        );
        
        if (variantForLanguage) {
          store.dispatch(setCurrentVariant(variantForLanguage));
          store.dispatch(setCurrentRegionalCode(variantForLanguage.code));
        }
      }
    }
  } else {
    console.warn("Unsupported language or variant:", lng);
  }
});

export default i18n;
